import { ApolloCache, MutationOptions } from '@apollo/client';
import { Required } from 'utility-types';

import { SelectedMedia, prepareMediaFile } from '@pletly/common';
import { PrivacyStatus } from '%constants/PrivacyStatus';
import { User } from '%context';
import {
	GetCurrentUserPosts,
	GetCurrentUserPostsVariables,
} from '%context/Posts/__generated__/GetCurrentUserPosts';
import {
	GetInformationFeedDataDocument,
	GetInformationFeedDataQuery,
	GetInformationFeedDataQueryVariables,
} from '%screens/profile/InformationFeed/__generated__/feed.generated';
import {
	mapMediaForOptimisticResponse,
	mapSelectedUsersForOptimisticResponse,
} from '%helpers/optimisticResponse';
import { CommunityGroupDocument } from '%screens/communities/components/__generated__/group.generated';
import {
	PostWithReviewFragment,
	PostWithReviewFragmentDoc,
} from '%screens/posts/review/components/__generated__/postsToReview.generated';
import { GetCurrentUserPostsDocument } from '%context/Posts/__generated__/userPosts.generated';
import {
	CreateNewPostMutation,
	CreateNewPostDocument,
	CreateNewPostMutationVariables,
} from '%components/Posts/Post/__generated__/createPost.mutation.generated';
import {
	UpdatePostDocument,
	UpdatePostMutation,
	UpdatePostMutationVariables,
} from '%components/Posts/Post/__generated__/updatePost.mutation.generated';
import { PostReviewStatus, PostType } from '%__generated__/types';
import { client } from '../../../../api/apollo';

type CreatePostData = Pick<
	CreateNewPostMutationVariables,
	| 'content'
	| 'selectedUsers'
	| 'informationFeedId'
	| 'communityGroupUuid'
	| 'organisationTeamsIds'
	| 'status'
	| 'organisationUuid'
	| 'publishAt'
> & {
	selectedMedia: SelectedMedia[];
	author: User;
};

type UpdatePostData = Pick<
	UpdatePostMutationVariables,
	'id' | 'content' | 'selectedUsers' | 'publishAt'
> & {
	selectedMedia: SelectedMedia[];
};

type CreateMutationOptions = {
	skipOptimisticResponse?: boolean;
	noCacheUpdate?: boolean;
	refetchQueries?: MutationOptions<CreateNewPostMutation>['refetchQueries'];
};

type UpdateMutationOptions = {
	removeFromCache?: boolean;
	refetchQueries?: MutationOptions<UpdatePostMutation>['refetchQueries'];
};

export type PostMutationsOptions = {
	create?: CreateMutationOptions;
	update?: UpdateMutationOptions;
};

export const getPostMutation = (
	postData: CreatePostData | UpdatePostData,
	options?: PostMutationsOptions,
) => {
	const now = Date.now();

	if ('id' in postData && postData.id) {
		return getUpdatePostMutation(postData, options?.update);
	}

	return getCreatePostMutation(postData as CreatePostData, now, options?.create);
};

const postFields = {
	status: PrivacyStatus.SelectedUsers,
};

const getCreatePostMutation = async (
	{
		content,
		selectedUsers,
		informationFeedId,
		selectedMedia,
		author,
		communityGroupUuid,
		organisationTeamsIds,
		status,
		organisationUuid,
		publishAt,
	}: CreatePostData,
	timestamp: number,
	options: CreateMutationOptions = {},
) => {
	const mediaFiles = selectedMedia
		? await Promise.all(selectedMedia.map(prepareMediaFile))
		: undefined;

	const { skipOptimisticResponse, noCacheUpdate, refetchQueries } = options;

	const defaultRefetchQueries = communityGroupUuid
		? [CommunityGroupDocument]
		: informationFeedId
			? [GetInformationFeedDataDocument]
			: [GetCurrentUserPostsDocument];

	return client.mutate({
		mutation: CreateNewPostDocument,
		variables: {
			...postFields,
			status: status ? status : PrivacyStatus.SelectedUsers,
			content,
			selectedUsers,
			informationFeedId,
			communityGroupUuid,
			organisationUuid,
			organisationTeamsIds,
			media: mediaFiles,
			publishAt,
		},
		refetchQueries: refetchQueries || defaultRefetchQueries,
		update: (cacheProxy, { data: mutationData }) => {
			if (noCacheUpdate || !mutationData || communityGroupUuid) {
				return;
			}

			informationFeedId
				? updateInformationFeedCache(cacheProxy, mutationData, informationFeedId)
				: updateCurrentUserPostsCache(cacheProxy, mutationData);
		},
		optimisticResponse: !skipOptimisticResponse
			? {
					createPost: {
						__typename: 'Post',
						id: `temp-id-${timestamp}}`,
						uuid: `temp-uuid-${timestamp}}`,
						createdAt: timestamp,
						updatedAt: timestamp,
						publishedAt: timestamp,
						comments: [],
						likes: [],
						selectedUsers: [],
						media: null,
						communityGroupId: null,
						author: {
							__typename: 'User',
							id: author.id,
							uuid: author.uuid,
							email: author.email,
							username: author.username,
							firstName: author.firstName || null,
							lastName: author.lastName || null,
							photoUrl: author.photoUrl || null,
						},
						pinned: false,
						status: postFields.status,
						type: informationFeedId ? PostType.Information : PostType.Feed,
						content,
						mediaFiles: mapMediaForOptimisticResponse(selectedMedia, timestamp),
						informationFeedId: informationFeedId || null,
					},
				}
			: undefined,
	});
};

const getReviewForOptimisticResponse = (postId: string) => {
	const ref = client.cache.identify({ __typename: 'Post', id: postId });

	const frag = client.cache.readFragment<PostWithReviewFragment>({
		id: ref,
		fragment: PostWithReviewFragmentDoc,
		fragmentName: 'PostWithReview',
	});

	if (!frag?.review) {
		return null;
	}

	return {
		...frag.review,
		__typename: 'PostReview' as const,
		status: PostReviewStatus.Pending,
	};
};

const getUpdatePostMutation = async (
	{ id, content, selectedMedia, selectedUsers, publishAt }: UpdatePostData,
	options: UpdateMutationOptions = {},
) => {
	const now = Date.now();

	const newlySelectedMedia = selectedMedia.filter((media) => !media.id);
	const newMedia = newlySelectedMedia
		? await Promise.all(newlySelectedMedia.map(prepareMediaFile))
		: undefined;
	const existingSelectedMedia = selectedMedia
		.filter((media): media is Required<typeof media, 'id'> => !!media.id)
		.map((media) => media.id);

	const review = getReviewForOptimisticResponse(id);

	const { removeFromCache, refetchQueries } = options;

	return client.mutate<UpdatePostMutation, UpdatePostMutationVariables>({
		mutation: UpdatePostDocument,
		variables: {
			id,
			content,
			selectedUsers,
			selectedExistingMedia: existingSelectedMedia,
			newMedia: newMedia,
			publishAt,
			...postFields,
		},
		optimisticResponse: {
			updatePost: {
				__typename: 'Post',
				id,
				content,
				mediaFiles: mapMediaForOptimisticResponse(selectedMedia, now),
				selectedUsers: mapSelectedUsersForOptimisticResponse(selectedUsers),
				review,
			},
		},
		refetchQueries,
		update: (cacheProxy, { data: mutationData }) => {
			if (!mutationData || !removeFromCache) {
				return;
			}
			const ref = cacheProxy.identify({ __typename: 'Post', id });
			cacheProxy.evict({ id: ref });
		},
	});
};

const updateInformationFeedCache = (
	cacheProxy: ApolloCache<(typeof client)['cache']>,
	mutationData: CreateNewPostMutation,
	informationFeedId: string,
) => {
	const postsInCache = cacheProxy.readQuery<
		GetInformationFeedDataQuery,
		GetInformationFeedDataQueryVariables
	>({
		query: GetInformationFeedDataDocument,
		variables: {
			id: informationFeedId,
		},
	});
	if (!postsInCache) {
		return;
	}
	const { createPost: createdPost } = mutationData;

	cacheProxy.writeQuery({
		query: GetInformationFeedDataDocument,
		data: {
			...postsInCache,
			informationFeed: {
				...postsInCache.informationFeed,
				posts: {
					...postsInCache.informationFeed.posts,
					edges: [
						{
							cursor: `temp-cursor-information-${createdPost.id}`,
							node: createdPost,
						},
						...(postsInCache.informationFeed.posts?.edges || []),
					],
				},
			},
		},
	});
};

const updateCurrentUserPostsCache = (
	cacheProxy: ApolloCache<(typeof client)['cache']>,
	mutationData: CreateNewPostMutation,
) => {
	const postsInCache = cacheProxy.readQuery<GetCurrentUserPosts, GetCurrentUserPostsVariables>({
		query: GetCurrentUserPostsDocument,
	});
	if (!postsInCache) {
		return;
	}

	const { createPost: createdPost } = mutationData;

	cacheProxy.writeQuery({
		query: GetCurrentUserPostsDocument,
		data: {
			...postsInCache,
			currentUser: {
				...postsInCache.currentUser,
				posts: {
					...postsInCache.currentUser.posts,
					edges: [
						{
							cursor: `temp-cursor-${createdPost.id}`,
							node: createdPost,
						},
						...(postsInCache.currentUser.posts?.edges || []),
					],
				},
			},
		},
	});
};
